import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { v1 as uuidv1 } from 'uuid';
import classnames from 'classnames';
import { Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import QuestionContainer from './QuestionContainer';
import {
  translateQuestionTypes, defaultQuestion, getQuestion, QuestionShape,
} from './questionTypesConsts';
import useStyles from './styles';
import QuestionChoiceOptions from './QuestionChoiceOptions';


const NChoiceQuestion = ({
  question: propQuestion, multiple, onConfirm, onGoBack, onHide, editMode,
}) => {
  const { t } = useTranslation(['translation']);
  const question = getQuestion(propQuestion);
  const [name, setQuestionName] = useState(question.name);
  const [options, setOptions] = useState(question.options);
  const [error, setError] = useState(null);

  const {
    SINGLE_CHOICE_QUESTION,
    MULTIPLE_CHOICE_QUESTION,
  } = translateQuestionTypes(t);

  const removeOption = id => {
    setOptions(options.filter(opt => opt.id !== id));
  };

  const addOption = () => {
    setError(null);
    setOptions([...options, {
      id: uuidv1(),
      name: '',
    }]);
  };

  const onChangeQuestionName = ev => {
    setError(null);
    setQuestionName(ev.target.value);
  };

  const changeOption = (id, value) => {
    setError(null);
    setOptions(options.map(
      opt => (opt.id === id ? { ...opt, name: value } : opt),
    ));
  };

  const onOptionDragEnd = ({ destination, source, draggableId }) => {
    const targetOption = options.find(opt => opt.id === draggableId);
    const newOptions = Array.from(options);
    newOptions.splice(source.index, 1);
    newOptions.splice(destination.index, 0, targetOption);
    setOptions(newOptions);
  };

  const saveQuestion = () => {
    if (options.filter(opt => opt.name !== '').length === 0) {
      setError(t('CAMPAIGN.QUESTIONNAIRE.EMPTY_OPTIONS_ERROR'));
      return;
    }
    if (name === '') {
      setError(t('CAMPAIGN.QUESTIONNAIRE.EMPTY_QUESTION_ERROR'));
      return;
    }
    onConfirm({
      id: uuidv1(),
      name,
      options,
      type: multiple ? MULTIPLE_CHOICE_QUESTION : SINGLE_CHOICE_QUESTION,
    });
  };

  const title = multiple ? MULTIPLE_CHOICE_QUESTION : SINGLE_CHOICE_QUESTION;

  const classes = useStyles();
  return (
    <QuestionContainer
      title={title}
      onGoBack={onGoBack}
      saveQuestion={saveQuestion}
      onHide={onHide}
    >
      <React.Fragment>
        <div className={classes.questionContainer}>
          <input
            className={classnames(classes.input, classes.questionInput)}
            value={name}
            onChange={onChangeQuestionName}
            placeholder={t('CAMPAIGN.QUESTIONNAIRE.QUESTION_PLACEHOLDER')}
            disabled={editMode}
          />
        </div>
        <div>
          <h4 className={classes.optionsHeader}>Opciones</h4>
          <div className={classes.optionsList}>
            <QuestionChoiceOptions
              options={options}
              onChange={changeOption}
              onDelete={removeOption}
              onDragEnd={onOptionDragEnd}
              multiple={multiple}
              editMode={editMode}
            />
            {!editMode && (
              <button
                className={classes.addAnotherOption}
                type="button"
                onClick={addOption}
              >
                {t('CAMPAIGN.QUESTIONNAIRE.ADD_OPTION')}
              </button>
            )}
          </div>
        </div>
        { error && <Alert color="danger">{error}</Alert> }
      </React.Fragment>
    </QuestionContainer>
  );
};


NChoiceQuestion.defaultProps = {
  question: defaultQuestion,
  multiple: false,
};

NChoiceQuestion.propTypes = {
  question: QuestionShape,
  multiple: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
};

export default NChoiceQuestion;

/* eslint-disable react/jsx-props-no-spreading */
export const SingleChoiceQuestion = props => <NChoiceQuestion {...props} />;
export const MultipleChoiceQuestion = props => <NChoiceQuestion multiple {...props} />;
